import React, { useMemo } from "react";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { OvertimeRequest } from "@/types";
import { OvertimeStatus } from "@/types/enums";
import { format as formatDate, isAfter, isBefore, isEqual } from "date-fns";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";

interface DailyOvertimeDepartmentTableProps {
  overtimeRequests: OvertimeRequest[];
  startDate?: Date;
  endDate?: Date;
  isLoading?: boolean;
}

// Define interface for table rows
interface OvertimeTableRow {
  date: string;
  total: number;
  [department: string]: string | number;
}

const getAllDatesInRange = (start?: Date, end?: Date) => {
  if (!start || !end) return [];
  const dates = [];
  const current = new Date(start);
  current.setHours(0, 0, 0, 0);
  const last = new Date(end);
  last.setHours(0, 0, 0, 0);
  while (current <= last) {
    dates.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  return dates;
};

// Update exportTableToCSV to use OvertimeTableRow[]
function exportTableToCSV(rows: OvertimeTableRow[], departments: string[]) {
  if (!rows.length) return;
  const header = ["Date", ...departments, "Total"];
  const csvRows = [header.join(",")];
  rows.forEach(row => {
    const values = [row.date, ...departments.map(dept => row[dept] ?? 0), row.total.toFixed(1)];
    csvRows.push(values.join(","));
  });
  const csvContent = csvRows.join("\r\n");
  const blob = new Blob([csvContent], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "DailyOvertimeDepartmentTable.csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const DailyOvertimeDepartmentTable: React.FC<DailyOvertimeDepartmentTableProps> = ({
  overtimeRequests,
  startDate,
  endDate,
  isLoading,
}) => {
  // Filter only approved requests in date range
  const filteredRequests = useMemo(() => {
    if (!overtimeRequests || !Array.isArray(overtimeRequests)) return [];
    return overtimeRequests.filter((req) => {
      if (req.status !== OvertimeStatus.APPROVED) return false;
      const reqDate = req.date ? new Date(req.date) : undefined;
      if (!reqDate || !startDate || !endDate) return false;
      reqDate.setHours(0, 0, 0, 0);
      return (
        (isEqual(reqDate, startDate) || isAfter(reqDate, startDate)) &&
        (isEqual(reqDate, endDate) || isBefore(reqDate, endDate))
      );
    });
  }, [overtimeRequests, startDate, endDate]);

  // Get all unique departments
  const departments = useMemo(() => {
    const set = new Set<string>();
    filteredRequests.forEach((req) => {
      if (req.department) set.add(req.department);
    });
    return Array.from(set).sort();
  }, [filteredRequests]);

  // Aggregate data by date and department
  const rows = useMemo(() => {
    if (!startDate || !endDate) return [];
    const allDates = getAllDatesInRange(startDate, endDate);
    const data: OvertimeTableRow[] = [];
    allDates.forEach((dateObj) => {
      const dateStr = formatDate(dateObj, "yyyy-MM-dd");
      const displayDate = formatDate(dateObj, "MMM dd, yyyy");
      let total = 0;
      const row: OvertimeTableRow = { date: displayDate, total: 0 };
      departments.forEach((dept) => {
        const sum = filteredRequests
          .filter((req) => req.department === dept && req.date === dateStr)
          .reduce((acc, req) => acc + (req.hours || 0), 0);
        row[dept] = sum > 0 ? sum.toFixed(1) : "";
        total += sum;
      });
      row.total = total;
      if (total > 0) data.push(row);
    });
    return data;
  }, [filteredRequests, startDate, endDate, departments]);

  if (isLoading) {
    return <div className="p-8 text-center">Loading table...</div>;
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-xl font-bold text-left">Daily Overtime by Department</CardTitle>
        <Button
          variant="outline"
          size="sm"
          className="flex items-center gap-2"
          onClick={() => exportTableToCSV(rows, departments)}
          disabled={rows.length === 0}
        >
          <Download className="h-4 w-4" />
          Export CSV
        </Button>
      </CardHeader>
      <CardContent>
        <Table>
          <TableCaption>Daily overtime hours by department.</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[120px]">Date</TableHead>
              {departments.map((dept) => (
                <TableHead key={dept}>{dept}</TableHead>
              ))}
              <TableHead>Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={departments.length + 2} className="text-center text-muted-foreground">
                  No overtime data for the selected period.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.date as string}>
                  <TableCell className="font-medium">{row.date}</TableCell>
                  {departments.map((dept) => (
                    <TableCell key={dept}>{row[dept]}</TableCell>
                  ))}
                  <TableCell>{row.total > 0 ? row.total.toFixed(1) : ""}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
          {rows.length > 0 && (
            <TableFooter>
              <TableRow>
                <TableCell colSpan={departments.length + 1}>Grand Total</TableCell>
                <TableCell>
                  {rows
                    .reduce((acc, row) => acc + row.total, 0)
                    .toFixed(1)}
                </TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </CardContent>
    </Card>
  );
};

export default DailyOvertimeDepartmentTable; 